export const getTouchedObject = (changes, templateContentId) => {
    return changes.find((changedObj) => Number.parseInt(changedObj.key, 10) === Number.parseInt(templateContentId, 10));
};

export const getSecondSemEo = (gradeObj, standardKey, gleKey, eoKey) => {
    return gradeObj.cdeStandards
        .find((std) => std.key === standardKey)
        .gles.find((semTwoGle) => semTwoGle.key === gleKey)
        .evidenceOutcomes.find((semTwoEo) => semTwoEo.key === eoKey);
};

export const getSecondSemChild = (gradeObj, standardKey, gleKey, eoKey, childKey) => {
    return gradeObj.cdeStandards
        .find((std) => std.key === standardKey)
        .gles.find((semTwoGle) => semTwoGle.key === gleKey)
        .evidenceOutcomes.find((semTwoEo) => semTwoEo.key === eoKey)
        .evidenceOutcomeChildList.find((semTwoChild) => semTwoChild.key === childKey);
};

export const getChecked = (touchedObj, obj) => {
    return touchedObj ? touchedObj.included : obj.included;
};

/**
 * Compute validation state for the define-reporting-periods form.
 *
 * Rule 1: every reportable item must be included in at least one semester.
 * Rule 2: at least one reportable item must be included in 1st semester AND
 *         at least one in 2nd semester (can't assign everything to one side).
 *
 * @param {Array}  cdeStandards                     - first-semester standards array
 * @param {object} secondSemesterProfileSelectedGrade - second-semester grade profile
 * @param {Array}  changes                           - pending user changes array
 * @returns {{ uncheckedKeys: Set, hasFirstSem: boolean, hasSecondSem: boolean, valid: boolean }}
 */
export const computeValidation = (cdeStandards, secondSemesterProfileSelectedGrade, changes) => {
    const uncheckedKeys = new Set();
    let hasFirstSem = false;
    let hasSecondSem = false;

    if (!secondSemesterProfileSelectedGrade?.cdeStandards) {
        return { uncheckedKeys, hasFirstSem, hasSecondSem, validForComplete: true, bothSemestersHaveItems: true };
    }

    const checkItem = (firstItem, secondItem) => {
        if (!firstItem.reportFlag) return;
        const firstChecked = getChecked(getTouchedObject(changes, firstItem.templateContentId), firstItem);
        const secondChecked = getChecked(getTouchedObject(changes, secondItem.templateContentId), secondItem);
        if (!firstChecked && !secondChecked) {
            uncheckedKeys.add(firstItem.key ?? firstItem.gradeToContentKey);
        }
        if (firstChecked) hasFirstSem = true;
        if (secondChecked) hasSecondSem = true;
    };

    const checkEoChildren = (eo, standardKey, gleKey) => {
        eo.evidenceOutcomeChildList?.forEach((eoChild) => {
            const secChild = getSecondSemChild(
                secondSemesterProfileSelectedGrade,
                standardKey,
                gleKey,
                eo.key,
                eoChild.key
            );
            checkItem(eoChild, secChild);
        });
    };

    const checkEos = (gle, standardKey) => {
        gle.evidenceOutcomes?.forEach((eo) => {
            const secEo = getSecondSemEo(secondSemesterProfileSelectedGrade, standardKey, gle.key, eo.key);
            checkItem(eo, secEo);
            checkEoChildren(eo, standardKey, gle.key);
        });
    };

    const checkGles = (standard) => {
        standard.gles.forEach((gle) => {
            const secGle = secondSemesterProfileSelectedGrade.cdeStandards
                .find((std) => std.key === standard.key)
                .gles.find((g) => g.key === gle.key);
            checkItem(gle, secGle);
            checkEos(gle, standard.key);
        });
    };

    cdeStandards.forEach((standard) => {
        const secStd = secondSemesterProfileSelectedGrade.cdeStandards.find(
            (std) => std.gradeToContentKey === standard.gradeToContentKey
        );
        const stdFirst = { ...standard, key: standard.gradeToContentKey };
        const stdSecond = { ...secStd, key: secStd.gradeToContentKey };
        checkItem(stdFirst, stdSecond);
        checkGles(standard);
    });

    const anyReportable = uncheckedKeys.size > 0 || hasFirstSem || hasSecondSem;
    // validForComplete: false when any item is unassigned to either semester — blocks "Save and Complete" and drives the error banner.
    // bothSemestersHaveItems: false when all items are in only one semester — drives warning banners only.
    const validForComplete = uncheckedKeys.size === 0;
    const bothSemestersHaveItems = validForComplete && (!anyReportable || (hasFirstSem && hasSecondSem));

    return { uncheckedKeys, hasFirstSem, hasSecondSem, validForComplete, bothSemestersHaveItems };
};
